import { useEffect, useRef, useState } from "react";
import { useLocation, useSearch } from "wouter";
import { COUNTRY_PARAM_WIDEN } from "@/lib/browse-country-anchor";

export interface InferredMarketFallbackInput {
  anchorCountry: string | null;
  hasLocation: boolean;
  hasBounds: boolean;
  loading: boolean;
  error: boolean;
  total: number | undefined;
  searchEverywhere: () => void;
}

export function shouldFallBackToEverywhere(
  input: InferredMarketFallbackInput & { pathname: string; hasCountryParam: boolean },
): boolean {
  return /^\/search\/?$/.test(input.pathname) &&
    !input.hasCountryParam && !input.hasLocation && !input.hasBounds &&
    !!input.anchorCountry && !input.loading && !input.error && input.total === 0;
}

/** Only a completed, implicitly anchored browse may opt into Search everywhere. */
export function useInferredMarketFallback(input: InferredMarketFallbackInput): boolean {
  const [pathname] = useLocation();
  const search = useSearch();
  const params = new URLSearchParams(search);
  const hasCountryParam = params.has("country") || params.has("country_code");
  const country = params.get("country");
  const attempted = useRef(false);
  const confirmedEverywhere = useRef(false);
  const [used, setUsed] = useState(false);

  useEffect(() => {
    if (used && country === COUNTRY_PARAM_WIDEN) confirmedEverywhere.current = true;
    if (used && ((confirmedEverywhere.current && country !== COUNTRY_PARAM_WIDEN) || input.hasLocation ||
        input.hasBounds || !/^\/search\/?$/.test(pathname))) {
      setUsed(false);
      attempted.current = false;
      confirmedEverywhere.current = false;
    }
    if (attempted.current || !shouldFallBackToEverywhere({
      ...input, pathname, hasCountryParam,
    })) return;
    attempted.current = true;
    setUsed(true);
    input.searchEverywhere();
    void import("@/lib/analytics").then(({ track }) => {
      track("search_inferred_market_fallback", { country: input.anchorCountry, scope: "all" });
    });
  }, [pathname, country, hasCountryParam, input.anchorCountry, input.hasLocation,
    input.hasBounds, input.loading, input.error, input.total, input.searchEverywhere, used]);

  return used && country === COUNTRY_PARAM_WIDEN &&
    !input.hasLocation && !input.hasBounds && /^\/search\/?$/.test(pathname);
}
